import { evaluate } from "../generation/evaluate";
import { isMathNode, toTex } from "../generation/operations";
import {
  InputPart,
  InputTypes,
  LatexPart,
  MrkdwnPart,
  NewLinePart,
  StepDelimiterPart,
  TemplatePart,
  TemplatePartTypes
} from "./types";
import {
  extractInputs,
  extractLatex,
  extractMarkdown,
  extractStatementParts,
  extractTextParts
} from "./utils/extractHelpers";
import { extractUsingRegex } from "./utils/extractUsingRegex";
import { processStatements } from "./utils/processStatements";

export default function parseTemplate(
  scope: any,
  template: string,
  performEvaluation: boolean = true
): TemplatePart[] {
  const inputParts = extractInputs(template).map(
    ({ original, sanitised, templatePosition }) => {
      const evaluated = evaluate(scope, sanitised);

      if (!!evaluated?.inputType) {
        return {
          type: TemplatePartTypes.INPUT,
          templatePosition,
          original,
          ...evaluated
        };
      }

      return {
        type: TemplatePartTypes.INPUT,
        inputType: InputTypes.REGULAR,
        templatePosition,
        original,
        correctValue: evaluated
      } as InputPart;
    }
  );

  const latexParts = extractLatex(template).map(
    ({ original, sanitised, templatePosition }) =>
      ({
        type: TemplatePartTypes.LATEX,
        original,
        templatePosition,
        value: processStatements(scope, sanitised, performEvaluation)
      }) as LatexPart
  );

  if (performEvaluation) {
    const exprStatementParts = extractStatementParts(template)
      .map(it => ({
        ...it,
        value: evaluate(scope, it.statement)
      }))
      .filter(it => isMathNode(it.value))
      .map(
        it =>
          ({
            ...it,
            type: TemplatePartTypes.LATEX,
            value: toTex(it.value)
          }) as LatexPart
      );

    latexParts.push(...exprStatementParts);
  }

  const mrkdwnParts = extractMarkdown(template).map(
    ({ original, sanitised, templatePosition }) =>
      ({
        type: TemplatePartTypes.MARKDOWN,
        original,
        templatePosition,
        value: processStatements(scope, sanitised, performEvaluation)
      }) as MrkdwnPart
  );

  const stepDelimiterParts = extractUsingRegex(template, /---+/g).map(
    ({ original, templatePosition }) =>
      ({
        type: TemplatePartTypes.STEP_DELIMITER,
        templatePosition,
        original
      }) as StepDelimiterPart
  );

  const newlineParts = extractUsingRegex(template, /\n+/g).map(
    ({ original, templatePosition }) =>
      ({
        type: TemplatePartTypes.NEWLINE,
        templatePosition,
        original
      }) as NewLinePart
  );

  const componentParts = [...inputParts, ...latexParts, ...mrkdwnParts];

  const delimiters = [...newlineParts, ...stepDelimiterParts].filter(
    delimiterPart =>
      // isn't inside another component
      !componentParts.some(
        componentPart =>
          componentPart.templatePosition <= delimiterPart.templatePosition &&
          componentPart.templatePosition + componentPart.original.length >
            delimiterPart.templatePosition
      )
  );

  const allPartsExceptText = [...delimiters, ...componentParts];

  const textParts = extractTextParts(template, allPartsExceptText).map(
    textPart => ({
      ...textPart,
      value: processStatements(scope, textPart.original, performEvaluation)
    })
  );

  const allParts = [...allPartsExceptText, ...textParts];

  return endTrimParts(
    allParts.sort((a, b) => a.templatePosition - b.templatePosition)
  );
}

function endTrimParts(parts: TemplatePart[]) {
  return parts.reduceRight<TemplatePart[]>((processedParts, part) => {
    if (
      processedParts.length === 0 &&
      (part.type === TemplatePartTypes.NEWLINE ||
        (part.type === TemplatePartTypes.TEXT &&
          part.value.trim().length === 0))
    ) {
      return processedParts;
    }

    return [part, ...processedParts];
  }, []);
}
